/**
 * Display helpers for coverage badges and the one-line coverage summary (REQ-INS-07).
 * Pure functions: the server derives every status and the summary; this only formats them.
 */
import type { ActivePayer, InsuranceSummary } from '../../../dtos/patientmr/insurance.dto';

export const MEDICAID_PROGRAM_LABELS: Record<string, string> = {
  FFS: 'Fee-for-Service (TMHP)',
  STAR: 'STAR',
  STAR_KIDS: 'STAR Kids',
  STAR_PLUS: 'STAR+PLUS',
  STAR_HEALTH: 'STAR Health',
  MCO: 'Managed care',
  CHIP: 'CHIP',
  CHIP_PERINATAL: 'CHIP Perinatal',
};

export const RELATIONSHIP_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'SELF', label: 'Self (the patient is the policyholder)' },
  { value: 'CHILD', label: "Child (policyholder is the patient's parent)" },
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'OTHER', label: 'Other' },
];

/** Relationship code for a stored value; legacy labels ('Self', 'Child', 'Parent') map to codes. */
export function normalizeRelationship(value?: string | null): string {
  if (!value) return '';
  const upper = value.trim().toUpperCase();
  if (['SELF', 'CHILD', 'SPOUSE', 'OTHER'].includes(upper)) return upper;
  if (['SON', 'DAUGHTER', 'DEPENDENT'].includes(upper)) return 'CHILD';
  if (['HUSBAND', 'WIFE'].includes(upper)) return 'SPOUSE';
  return 'OTHER';
}

const PAYER_BADGE_CLASS: Record<string, string> = {
  Medicaid: 'bg-green-100 text-green-800',
  Private: 'bg-blue-100 text-blue-800',
  Medicare: 'bg-purple-100 text-purple-800',
  Other: 'bg-slate-100 text-slate-800',
};

export function payerBadgeClass(coverageType: string): string {
  return PAYER_BADGE_CLASS[coverageType] || PAYER_BADGE_CLASS.Other;
}

/** Badge label: the coverage type, with "+" for secondary/tertiary (e.g. "+Medicaid"). */
export function payerBadgeLabel(payer: ActivePayer): string {
  return payer.cobOrder === 'Primary' ? payer.coverageType : `+${payer.coverageType}`;
}

function describePayer(payer: ActivePayer): string {
  const program = payer.program ? MEDICAID_PROGRAM_LABELS[payer.program] || payer.program : null;
  if (payer.coverageType === 'Medicaid') {
    return program ? `Medicaid (${program})` : 'Medicaid';
  }
  if (payer.coverageType === 'Private' || payer.coverageType === 'Other') {
    return `${payer.coverageType}: ${payer.payerName}`;
  }
  return payer.payerName || payer.coverageType;
}

/**
 * One-line coverage summary for the patient header, e.g.
 * "Private: Aetna · Medicaid (STAR Kids) secondary · Medicaid EVV".
 */
export function coverageSummaryLine(summary?: InsuranceSummary | null): string | null {
  if (!summary) return null;
  if (!summary.activePayers?.length) return 'No active coverage';
  const parts = summary.activePayers.map(p => (p.cobOrder === 'Primary' ? describePayer(p) : `${describePayer(p)} ${String(p.cobOrder).toLowerCase()}`));
  // Payer-level: Medicaid (not CHIP). Whether a visit needs EVV also depends on its service (R-177).
  if (summary.evvRequired) parts.push('Medicaid EVV');
  return parts.join(' · ');
}

/** True when the red "No active primary coverage" badge and banner apply (REQ-INS-07). */
export function lacksActivePrimary(summary?: InsuranceSummary | null): boolean {
  return Boolean(summary) && summary!.hasActivePrimary === false;
}

/** Days since a 'YYYY-MM-DD' date, against today's date in `today` ('YYYY-MM-DD'). */
export function daysSince(date: string, today: string): number {
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86400000);
}
